import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { generateSavedViewUrl } from "components/saved_searches/utils";
import Button from "components/ui/button/Button";
import { emptyStateIcons } from "components/ui/empty_state/EmptyStateIcons";
import { useSavedViewsDataType } from "lib/hooks/api/saved_views/useSavedViews";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { SavedViewDataType, SavedViewSortBy, SavedViewSortOrder } from "lib/types/graphQLEnums";
import { NOTICE_V2_TOPIC_SEARCH_PLACEHOLDER } from "./TopicSearchSection";

export type EmptyNoticesV2Variant = "beforeSearch" | "noResults";

export type EmptyNoticesV2Props = {
  variant?: EmptyNoticesV2Variant;
  onSelectPinnedSearch: (viewId: string, viewName: string) => void;
};

export function EmptyNoticesV2({
  variant = "beforeSearch",
  onSelectPinnedSearch,
}: EmptyNoticesV2Props): JSX.Element {
  const isNoResults = variant === "noResults";
  const { data: pinnedData, isLoading: pinnedLoading } = useSavedViewsDataType(
    SavedViewDataType.NoticeSemantic,
    {
      pinned: true,
      orderBy: SavedViewSortBy.PinnedRank,
      order: SavedViewSortOrder.Asc,
    },
    { page: 1, pageSize: 50 },
  );

  const pinnedViews = pinnedData?.results ?? [];

  return (
    <Flex width="100%" height="100%" alignItems="center" justifyContent="center">
      <Panel column gap={24} alignItems="center">
        <Flex column gap={24} alignItems="center">
          <Flex column gap={8} alignItems="center">
            <UIcon
              svg={isNoResults ? emptyStateIcons.noResults : emptyStateIcons.startingState}
              size={110}
            />
            <Flex column gap={4} alignItems="center">
              <StartSearchHeading as="h2">
                {isNoResults ? "No notices match your search" : "Start new search"}
              </StartSearchHeading>
              <TopicHint description center color="sysTextSecondary">
                {isNoResults
                  ? "Try a different topic or adjust your filters."
                  : NOTICE_V2_TOPIC_SEARCH_PLACEHOLDER}
              </TopicHint>
            </Flex>
          </Flex>
        </Flex>

        <PinnedColumn column gap={24} alignItems="center">
          <PinnedTitle fontWeight="500">Pinned notice searches</PinnedTitle>
          {!pinnedLoading && pinnedViews.length === 0 && (
            <Flex gap={8} alignItems="center" justifyContent="center">
              <UIcon icon="pin02" size={14} color="sysTextInactive" />
              <Text fontWeight="400" color="sysTextInactive">
                No pinned searches yet
              </Text>
            </Flex>
          )}
          {!pinnedLoading && pinnedViews.length > 0 && (
            <Flex column gap={8} alignItems="stretch" width="100%" role="list">
              {pinnedViews.map((view) => (
                <Button
                  key={view.id}
                  buttonType="tertiary"
                  as="a"
                  role="listitem"
                  href={generateSavedViewUrl(view.id, SavedViewDataType.NoticeSemantic)}
                  onClick={(e) => {
                    e.preventDefault();
                    onSelectPinnedSearch(view.id, view.name);
                  }}
                >
                  {view.name}
                </Button>
              ))}
            </Flex>
          )}
        </PinnedColumn>
      </Panel>
    </Flex>
  );
}

const Panel = styled(Flex)(({ theme }) => ({
  width: "100%",
  maxWidth: 640,
  backgroundColor: theme.colors.sysBackgroundDefault,
  borderRadius: 4,
  padding: "64px 48px",
  boxSizing: "border-box",
}));

const StartSearchHeading = styled(Text)({
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.25,
  textAlign: "center",
  width: "100%",
  maxWidth: 298,
});

const TopicHint = styled(Text)({
  textAlign: "center",
  width: "100%",
  maxWidth: 298,
});

const PinnedColumn = styled(Flex)({
  width: "100%",
  maxWidth: 257,
});

const PinnedTitle = styled(Text)({
  fontSize: 14,
  lineHeight: 1.5,
  textAlign: "center",
});
